import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  executeSync,
  fetchKeycloakConfig,
  fetchKeycloakGroups,
  fetchKeycloakUsers,
  fetchSyncStatus,
  saveKeycloakConfig,
  testKeycloakConnection,
  updateRoleMapping,
  type KeycloakConfigStatus,
  type KeycloakGroup,
  type KeycloakUser,
  type SyncResult,
  type SyncStatus,
  type TestConnectionResult,
} from '../services/keycloakSync';

export const kcSyncKeys = {
  root: ['keycloak-sync'] as const,
  config: () => [...kcSyncKeys.root, 'config'] as const,
  users: (search?: string, groupId?: string) =>
    [...kcSyncKeys.root, 'users', search ?? '', groupId ?? ''] as const,
  groups: () => [...kcSyncKeys.root, 'groups'] as const,
  syncStatus: () => [...kcSyncKeys.root, 'sync-status'] as const,
};

export const useKeycloakConfig = () =>
  useQuery<KeycloakConfigStatus>(kcSyncKeys.config(), fetchKeycloakConfig, {
    retry: false,
  });

export const useSaveKeycloakConfig = () => {
  const client = useQueryClient();
  return useMutation(saveKeycloakConfig, {
    onSuccess: () => {
      client.invalidateQueries(kcSyncKeys.config());
    },
  });
};

export const useTestConnection = () =>
  useMutation<TestConnectionResult, unknown, void>(() =>
    testKeycloakConnection(),
  );

export const useKeycloakUsers = (search?: string, groupId?: string) =>
  useQuery<{ users: KeycloakUser[]; total: number }>(
    kcSyncKeys.users(search, groupId),
    () => fetchKeycloakUsers({ search, group_id: groupId }),
    { enabled: false, retry: false },
  );

export const useFetchKeycloakUsers = () =>
  useMutation(fetchKeycloakUsers);

export const useKeycloakGroups = () =>
  useQuery<{ groups: KeycloakGroup[] }>(
    kcSyncKeys.groups(),
    fetchKeycloakGroups,
    { enabled: false, retry: false },
  );

export const useExecuteSync = () => {
  const client = useQueryClient();
  return useMutation(executeSync, {
    onSuccess: () => {
      client.invalidateQueries(kcSyncKeys.syncStatus());
      client.invalidateQueries(kcSyncKeys.users());
    },
  });
};

export const useSyncStatus = () =>
  useQuery<SyncStatus>(kcSyncKeys.syncStatus(), fetchSyncStatus, {
    retry: false,
  });

export const useUpdateRoleMapping = () => {
  const client = useQueryClient();
  return useMutation(updateRoleMapping, {
    onSuccess: () => {
      client.invalidateQueries(kcSyncKeys.config());
    },
  });
};
